import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useThemeSwitcher } from "../theme";

type LoadingSize = "xs" | "sm" | "md" | "lg";

export interface LoadingIndicatorProps {
  size?: LoadingSize;
  text?: string;
  fullScreen?: boolean;
  className?: string;
}

const ordaSizeMap: Record<LoadingSize, { width: number; fontSize: string }> = {
  xs: { width: 72, fontSize: "0.75rem" },
  sm: { width: 140, fontSize: "0.75rem" },
  md: { width: 220, fontSize: "0.875rem" },
  lg: { width: 320, fontSize: "1rem" },
};
const sizeMap: Record<LoadingSize, { width: number; fontSize: string }> = {
  xs: { width: 72, fontSize: "0.75rem" },
  sm: { width: 140, fontSize: "1.5rem" },
  md: { width: 220, fontSize: "2rem" },
  lg: { width: 640, fontSize: "2rem" },
};

const animationStyles = `
  @keyframes loading-indicator-pulse {
    0%, 100% { opacity: 0.25; transform: translateY(0); }
    50% { opacity: 1; transform: translateY(-6px); }
  }
  .loading-indicator-pulse {
    animation: loading-indicator-pulse 1.2s ease-in-out infinite;
    transform-box: fill-box;
    transform-origin: center;
  }
  .loading-indicator-logo-wave {
    animation-duration: 1.4s;
  }
  .loading-indicator-delay-100 { animation-delay: 100ms; }
  .loading-indicator-delay-200 { animation-delay: 200ms; }
  .loading-indicator-delay-300 { animation-delay: 300ms; }
  .loading-indicator-delay-400 { animation-delay: 400ms; }
  .loading-indicator-delay-500 { animation-delay: 500ms; }
  .loading-indicator-delay-600 { animation-delay: 600ms; }
  .loading-indicator-delay-700 { animation-delay: 700ms; }
  .loading-indicator-delay-800 { animation-delay: 800ms; }
  .loading-indicator-delay-900 { animation-delay: 900ms; }
  .loading-indicator-delay-1000 { animation-delay: 1000ms; }
  .loading-indicator-text-pulse {
    animation: loading-indicator-pulse 1.2s ease-in-out infinite;
  }
`;

const OrdaLogo: React.FC<{ color: string; width: number }> = ({
  color,
  width,
}) => (
  <svg
    viewBox="0 0 783 156"
    style={{ color, width }}
    fill="currentColor"
    aria-hidden="true"
  >
    <g transform="translate(0,156) scale(0.1,-0.1)">
      <path
        className="loading-indicator-pulse loading-indicator-logo-wave"
        d="M1215 1446 c-319 -50 -534 -225 -599 -489 -24 -97 -23 -269 3 -370
        77 -299 348 -471 761 -484 367 -12 627 90 769 302 111 166 139 410 70 614 -60
        176 -211 318 -410 385 -159 53 -407 71 -594 42z m465 -141 c277 -64 430 -250
        430 -525 0 -269 -142 -447 -415 -522 -69 -19 -105 -21 -270 -21 -162 0 -201 3
        -268 21 -207 57 -329 161 -388 332 -18 53 -22 87 -23 185 0 127 9 172 51 262
        69 145 233 250 443 282 97 15 351 7 440 -14z"
      />
      <path
        className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-300"
        d="M2560 781 l0 -661 70 0 70 0 2 277 3 278 305 3 306 2 209 -280 209
        -280 89 0 c81 0 87 1 78 18 -6 9 -104 138 -219 287 l-210 270 54 16 c109 34
        186 97 228 188 17 38 21 65 21 156 0 94 -4 118 -24 162 -42 94 -128 157 -269
        195 -71 20 -109 22 -499 26 l-423 4 0 -661z m802 509 c200 -23 268 -82 268
        -232 -1 -168 -70 -219 -324 -239 -72 -5 -237 -9 -366 -7 l-235 3 -3 243 -2
        242 287 0 c159 0 327 -5 375 -10z"
      />
      <path
        className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-600"
        d="M4160 781 l0 -661 343 0 c375 0 522 9 641 40 221 56 364 192 422 402
        24 85 29 288 10 387 -46 246 -207 405 -466 463 -86 19 -134 21 -522 25 l-428
        5 0 -661z m899 505 c200 -41 319 -149 366 -331 20 -78 20 -272 0 -348 -44
        -170 -149 -272 -330 -323 -50 -15 -121 -18 -427 -22 l-368 -4 0 519 c0 286 3
        523 8 528 13 14 669 -3 751 -19z"
      />
      <path
        className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-900"
        d="M6341 1338 c-30 -57 -73 -139 -96 -183 -158 -299 -525 -1003 -530
        -1018 -6 -15 1 -17 68 -17 48 0 78 4 84 12 6 7 142 266 303 576 162 309 297
        562 301 562 4 0 82 -143 173 -317 91 -175 225 -433 299 -573 l133 -255 77 -3
        c42 -2 77 0 77 5 0 4 -154 301 -341 660 l-342 653 -76 0 -77 0 -53 -102z"
      />
    </g>
  </svg>
);

const GastrosoftLogo: React.FC<{ width: number }> = ({ width }) => (
  <svg
    viewBox="0 0 1500 130"
    style={{ width }}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      className="loading-indicator-pulse loading-indicator-logo-wave"
      fill="#004D97"
      d="M115.06,32.61h-75.8v66.11h75.8V75.47H75.44V57.68h63.28v35.97c0,9.71-1.82,16.25-5.47,19.61
      c-3.64,3.37-10.44,5.05-20.41,5.05H40.88c-9.98,0-16.79-1.68-20.42-5.05C16.82,109.9,15,103.36,15,93.66V38.67
      c0-9.7,1.82-16.24,5.46-19.6s10.45-5.05,20.42-5.05h71.96c9.71,0,16.44,1.65,20.22,4.95c3.77,3.31,5.66,9.54,5.66,18.7v2.43
      l-23.66,4.24V32.61z"
    />
    <path
      className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-100"
      fill="#004D97"
      d="M157.92,118.32l59.03-104.3h23.45l59.83,104.3h-27.28L260.4,95.48H194.3l-12.12,22.85H157.92z M204.61,76.68
      h45.69l-22.44-42.45L204.61,76.68z"
    />
    <path
      className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-200"
      fill="#004D97"
      d="M416.06,32.21h-68.53v22.44h65.23c9.99,0,16.85,1.68,20.56,5.05c3.72,3.37,5.57,9.91,5.57,19.6v14.35
      c0,9.71-1.85,16.25-5.56,19.61c-3.7,3.37-10.54,5.05-20.52,5.05h-64.48c-9.97,0-16.81-1.68-20.52-5.05
      c-3.7-3.37-5.55-9.91-5.55-19.61v-2.83l21.42-4.45v12.34h73.79V74.86h-65.1c-9.97,0-16.78-1.68-20.41-5.06
      c-3.64-3.37-5.46-9.91-5.46-19.6V38.67c0-9.7,1.82-16.24,5.46-19.6s10.45-5.05,20.41-5.05h59.24c9.56,0,16.27,1.61,20.11,4.84
      c3.84,3.24,5.76,9.24,5.76,17.99v2.22l-21.42,5.05V32.21z"
    />
    <path
      className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-300"
      fill="#004D97"
      d="M537.74,32.61v85.71h-24.26V32.61h-47.91V14.02h120.27v18.59H537.74z"
    />
    <path
      className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-400"
      fill="#004D97"
      d="M615.56,118.32V14.02h89.35c15.63,0,23.45,6.74,23.45,20.22v24.46c0,13.47-7.82,20.22-23.45,20.22h-13.74
      l48.11,39.41H704.5l-42.04-39.41h-23.04v39.41H615.56z M694,31.8h-54.58v29.31H694c3.77,0,6.4-0.41,7.89-1.22
      c2.02-1.23,3.02-3.6,3.02-7.13V40.15c0-3.52-1.01-5.9-3.02-7.12C700.4,32.21,697.77,31.8,694,31.8z"
    />
    <path
      className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-500"
      fill="#004D97"
      d="M763.32,38.67c0-9.7,1.82-16.24,5.46-19.6s10.45-5.05,20.42-5.05h76.81c9.97,0,16.78,1.68,20.41,5.05
      s5.46,9.91,5.46,19.6v54.98c0,9.71-1.82,16.25-5.46,19.61c-3.64,3.37-10.45,5.05-20.41,5.05H789.2c-9.98,0-16.79-1.68-20.42-5.05
      c-3.64-3.37-5.46-9.91-5.46-19.61V38.67z M787.58,98.72h80.05V32.61h-80.05V98.72z"
    />
    <path
      className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-600"
      fill="#353942"
      d="M1017.22,32.21h-68.53v22.44h65.23c9.99,0,16.85,1.68,20.56,5.05c3.72,3.37,5.57,9.91,5.57,19.6v14.35
      c0,9.71-1.85,16.25-5.56,19.61c-3.7,3.37-10.54,5.05-20.52,5.05h-64.48c-9.97,0-16.81-1.68-20.52-5.05
      c-3.7-3.37-5.55-9.91-5.55-19.61v-2.83l21.42-4.45v12.34h73.79V74.86h-65.1c-9.97,0-16.78-1.68-20.41-5.06
      c-3.64-3.37-5.46-9.91-5.46-19.6V38.67c0-9.7,1.82-16.24,5.46-19.6s10.45-5.05,20.41-5.05h59.24c9.56,0,16.27,1.61,20.11,4.84
      c3.84,3.24,5.76,9.24,5.76,17.99v2.22l-21.42,5.05V32.21z"
    />
    <path
      className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-700"
      fill="#353942"
      d="M1076.04,38.67c0-9.7,1.82-16.24,5.46-19.6c3.64-3.37,10.45-5.05,20.42-5.05h76.81
      c9.97,0,16.78,1.68,20.41,5.05c3.64,3.37,5.46,9.91,5.46,19.6v54.98c0,9.71-1.82,16.25-5.46,19.61c-3.64,3.37-10.45,5.05-20.41,5.05
      h-76.81c-9.98,0-16.79-1.68-20.42-5.05c-3.64-3.37-5.46-9.91-5.46-19.61V38.67z M1100.3,98.72h80.05V32.61h-80.05V98.72z"
    />
    <path
      className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-800"
      fill="#353942"
      d="M1243.61,118.32V14.02h105.52v18.19h-81.26V59.7h43.86v19h-43.86v39.62H1243.61z"
    />
    <path
      className="loading-indicator-pulse loading-indicator-logo-wave loading-indicator-delay-900"
      fill="#353942"
      d="M1439.9,32.61v85.71h-24.26V32.61h-47.91V14.02H1488v18.59H1439.9z"
    />
  </svg>
);

export const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  size = "md",
  text,
  fullScreen = false,
  className = "",
}) => {
  const theme = useTheme();
  const { currentBrand } = useThemeSwitcher();
  const { width, fontSize } =
    currentBrand === "orda-pos" ? ordaSizeMap[size] : sizeMap[size];
  const logo =
    currentBrand === "orda-pos" ? (
      <OrdaLogo color={theme.palette.text.primary} width={width} />
    ) : (
      <GastrosoftLogo width={width} />
    );

  const content = (
    <Box
      className={className}
      sx={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        justifyContent: "center",
      }}
    >
      {logo}

      {text ? (
        <Typography
          component="p"
          className="loading-indicator-text-pulse"
          sx={{
            color: "text.secondary",
            fontSize,
            fontWeight: 500,
          }}
        >
          {text}
        </Typography>
      ) : null}

      <style href="loading-indicator-styles" precedence="medium">
        {animationStyles}
      </style>
    </Box>
  );

  if (fullScreen) {
    return (
      <Box
        sx={{
          alignItems: "center",
          backdropFilter: "blur(4px)",
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(17, 24, 39, 0.8)"
              : "rgba(255, 255, 255, 0.8)",
          display: "flex",
          inset: 0,
          justifyContent: "center",
          position: "fixed",
          zIndex: theme.zIndex.modal,
        }}
      >
        {content}
      </Box>
    );
  }

  return content;
};

export default LoadingIndicator;
